import { createStyles, Stack, StackProps, Text, Title } from '@mantine/core';
import React, { ReactNode } from 'react';

import { ProductCategoryBadge } from './ProductCategoryBadge';
import { ProductImage } from './ProductImage';

interface ProductCardProps extends StackProps {}

export function ProductCard({
  children,
  className,
  ...stackProps
}: ProductCardProps) {
  const { classes, cx } = useProductCardStyles();

  return (
    <Stack
      spacing={0}
      className={cx(classes.container, className)}
      {...stackProps}
    >
      {children}
    </Stack>
  );
}

const useProductCardStyles = createStyles((theme) => ({
  container: {
    overflow: 'hidden',
    position: 'relative',
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.md,
    backgroundColor: theme.white,
  },
}));

interface ProductCardImageProps {
  imageUrl?: string | null;
  category?: string | null;
}
function ProductCardImage({ imageUrl, category }: ProductCardImageProps) {
  const { classes } = useProductCardImageStyles();
  return (
    <>
      <ProductImage src={imageUrl} data-testid="product-card-img" />

      {category ? (
        <ProductCategoryBadge
          className={classes.categoryBadge}
          category={category}
        />
      ) : null}
    </>
  );
}

const useProductCardImageStyles = createStyles((theme) => ({
  categoryBadge: {
    position: 'absolute',
    top: theme.spacing.xl,
    left: theme.spacing.xl,
  },
}));

interface ProductCardDetailsProps {
  name: string;
  subtitle?: string | null;
  children?: ReactNode;
}
function ProductCardDetails({
  name,
  subtitle,
  children,
}: ProductCardDetailsProps) {
  const { classes } = useProductCardDetailsStyles();

  return (
    <Stack
      spacing="xs"
      p="xl"
      justify="space-between"
      className={classes.container}
    >
      <Stack spacing={4}>
        <Title
          order={5}
          weight={400}
          truncate
          title={name}
          className={classes.title}
          data-testid="product-card-name"
        >
          {name}
        </Title>

        {subtitle ? (
          <Text
            color="gray.5"
            truncate
            weight={600}
            title={subtitle}
            data-testid="product-card-subtitle"
          >
            {subtitle}
          </Text>
        ) : null}
      </Stack>

      {children}
    </Stack>
  );
}

const useProductCardDetailsStyles = createStyles((theme) => ({
  container: {
    flexGrow: 1,
    borderTop: `1px solid ${theme.colors.gray[2]}`,
  },
  title: {
    [theme.fn.smallerThan('md')]: {
      fontSize: theme.fontSizes.md,
    },
  },
}));

ProductCard.Image = ProductCardImage;
ProductCard.Details = ProductCardDetails;
